import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ArrowRight, ShieldCheck, Clock, MapPin, Scale, AlertTriangle, Search, Award } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import RelatedCaseResults from '@/components/RelatedCaseResults';
import LegalConceptsCallout from '@/components/LegalConceptsCallout';

const CaseResultRetainedSurgicalToolPage = () => {
  const caseFacts = [
    { icon: Scale, label: 'Case type', value: 'Medical malpractice' },
    { icon: Award, label: 'Result', value: 'Confidential pre-litigation settlement' },
    { icon: AlertTriangle, label: 'Injury', value: 'Retained surgical-tool fragment requiring a second surgery' },
    { icon: MapPin, label: 'Location', value: 'Washington State' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="case-result-retained-surgical-tool">
      <SEOHead
        noindex={true}
        title="Confidential Settlement for Retained Surgical Tool | Amy C. Brown Law"
        description="Amy C. Brown obtained a confidential settlement after a broken surgical-tool fragment was left inside a patient, requiring a second surgery."
        pageType="article"
        articleData={{
          title: 'Confidential Settlement After Broken Surgical Tool Was Left in Patient',
          description: 'Case study: retained surgical-tool fragment following joint-replacement surgery in Washington State. Confidential pre-litigation settlement.',
          datePublished: '2026-08-26',
          dateModified: '2026-08-26',
          articleSection: 'Medical Malpractice Case Results',
          keywords: 'retained surgical object, medical malpractice, res ipsa loquitur, Washington personal injury, joint replacement complication, retained instrument, surgical tool fragment, confidential settlement',
          about: [
            { '@type': 'Thing', name: 'Medical Malpractice', sameAs: 'https://en.wikipedia.org/wiki/Medical_malpractice' },
            { '@type': 'Thing', name: 'Res ipsa loquitur', sameAs: 'https://en.wikipedia.org/wiki/Res_ipsa_loquitur' },
            { '@type': 'Thing', name: 'Retained surgical instrument', sameAs: 'https://en.wikipedia.org/wiki/Retained_surgical_instrument' }
          ]
        }}
        faqData={[
          {
            question: 'What is a retained surgical object?',
            answer: 'A retained surgical object is any instrument, sponge, needle, or fragment of an instrument left inside a patient after surgery. Washington courts recognize that this can support an inference of negligence under res ipsa loquitur — "the thing speaks for itself."',
          },
          {
            question: 'Does a correct surgical count mean the hospital was not negligent?',
            answer: 'No. A correct surgical count does not resolve the problem when a portion of an instrument breaks off and remains inside the patient. The hospital may still be responsible, and the manufacturer of a defective instrument may share liability.',
          },
          {
            question: 'What factors affect the value of a retained-object case?',
            answer: 'Key factors include what object was left inside, how long it remained there, the severity and duration of pain, whether additional surgery was required, whether infection or permanent injury occurred, whether a defective instrument contributed, and which providers, facilities, manufacturers, and insurers may be responsible.',
          },
        ]}
      />

      {/* Hero — abstract subtle graphic on the right */}
      <section className="pt-16 pb-14 md:pt-20 md:pb-20 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <nav className="mb-6" aria-label="Breadcrumb">
            <Link to="/med-malpractice" className="text-sm text-teal-700 hover:underline inline-flex items-center gap-1">
              <ArrowRight className="w-4 h-4 rotate-180" /> Medical Malpractice
            </Link>
          </nav>

          <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16 items-start">
            <div>
              <span className="inline-block bg-teal-50 text-teal-700 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5" data-testid="case-badge">
                Case Result · Medical Malpractice
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-secondary leading-tight mb-5">
                Confidential Settlement for Retained Surgical-Tool Fragment
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                A broken surgical-tool fragment caused five days of severe pain and required a second operation. Amy obtained a confidential settlement without filing a lawsuit.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="tel:425-675-1224"
                  data-testid="case-hero-call"
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition"
                >
                  <Phone className="w-4 h-4" /> Free Consultation
                </a>
                <a
                  href="sms:425-675-1224"
                  className="inline-flex items-center gap-2 border-2 border-slate-300 text-secondary px-6 py-3 rounded-full font-semibold hover:border-teal-600 hover:text-teal-700 transition"
                >
                  <MessageCircle className="w-4 h-4" /> Text Me
                </a>
              </div>
            </div>

            {/* Abstract graphic — scales + medical cross motif in brand palette */}
            <div className="hidden lg:block" aria-hidden="true">
              <svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                <defs>
                  <linearGradient id="cs-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#F5EEEB" />
                    <stop offset="100%" stopColor="#EAF3F3" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="320" height="320" rx="24" fill="url(#cs-bg)" />
                {/* Scales of justice — thin lines */}
                <g stroke="#4A9B9B" strokeWidth="2.5" strokeLinecap="round" fill="none">
                  <line x1="160" y1="70" x2="160" y2="230" />
                  <line x1="100" y1="110" x2="220" y2="110" />
                  <line x1="100" y1="110" x2="80" y2="160" />
                  <line x1="100" y1="110" x2="120" y2="160" />
                  <line x1="220" y1="110" x2="200" y2="160" />
                  <line x1="220" y1="110" x2="240" y2="160" />
                  <line x1="140" y1="230" x2="180" y2="230" />
                </g>
                <ellipse cx="100" cy="163" rx="26" ry="6" fill="#4A9B9B" opacity="0.15" />
                <ellipse cx="220" cy="163" rx="26" ry="6" fill="#4A9B9B" opacity="0.15" />
                {/* Subtle medical cross accent */}
                <g fill="#E8A0A8" opacity="0.85">
                  <rect x="152" y="46" width="16" height="16" rx="2" />
                  <rect x="146" y="52" width="28" height="4" rx="1.5" />
                  <rect x="158" y="46" width="4" height="16" rx="1.5" fill="#ffffff" opacity="0.9" />
                  <rect x="146" y="52" width="28" height="4" rx="1.5" fill="#ffffff" opacity="0.9" />
                </g>
                {/* Dot pattern */}
                <g fill="#4A9B9B" opacity="0.2">
                  {[0,1,2,3,4].map((r) => [0,1,2,3,4].map((c) => (
                    <circle key={`${r}-${c}`} cx={40 + c*12} cy={260 + r*12} r="1.5" />
                  )))}
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Case Facts sidebar strip */}
      <section className="py-10 bg-slate-50" data-testid="case-facts">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseFacts.map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-white rounded-2xl p-5 border border-slate-100">
                <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</p>
                <p className="text-secondary font-medium leading-snug">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full narrative */}
      <article className="py-16 md:py-20 bg-white" data-testid="case-narrative">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="prose prose-slate prose-lg max-w-none">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-0">What Happened</h2>
            <p className="lead text-lg text-slate-700 leading-relaxed">
              The client underwent a surgical procedure expecting to begin recovery. Instead, a piece of a surgical tool broke off and was left inside the body.
            </p>
            <p className="text-slate-700 leading-relaxed">
              The client experienced severe pain for days. Concerns were initially minimized before the broken tool fragment was discovered, and a second surgery was required to remove it — delaying the client's recovery from the original procedure.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">The Hospital Initially Denied Negligence</h2>
            <p className="text-slate-700 leading-relaxed">
              The hospital initially argued that it was not negligent because its surgical counts were correct and the tool itself had been accounted for. That argument ignored the central issue: part of the tool had broken off and remained inside the patient.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Washington courts recognize that leaving a foreign object inside a patient can support an inference of negligence under the doctrine of <em>res ipsa loquitur</em> — Latin for "the thing speaks for itself." A surgical count does not resolve the problem when a portion of an instrument is missing and remains inside the patient.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">Looking Beyond the Hospital</h2>
            <p className="text-slate-700 leading-relaxed">
              The medical records did not identify the manufacturer of the surgical instrument that broke. Because the tool should not have broken during the procedure, I investigated the manufacturer as another potentially responsible party — pursuing every available source until I identified it and ensured that all potential avenues of recovery were considered.
            </p>

            {/* Pull-quote highlight */}
            <blockquote className="not-prose my-10 border-l-4 border-teal-600 bg-teal-50/60 px-6 py-5 rounded-r-lg">
              <p className="text-lg md:text-xl text-secondary font-medium leading-snug italic">
                "I look beyond the most obvious defendant to determine who may share responsibility and where insurance coverage or other sources of recovery may exist."
              </p>
              <p className="text-sm text-slate-500 mt-3">— Amy C. Brown</p>
            </blockquote>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">The Result</h2>
            <p className="text-slate-700 leading-relaxed">
              I secured a confidential settlement for the client before filing a lawsuit. Because the case resolved during the pre-litigation process, the client did not have to endure a deposition, formal discovery, or trial. Related medical expenses were addressed, and the client received substantial compensation for the pain, the additional surgery, and the delayed recovery.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Without effective legal representation, the client may have received little more than payment of medical expenses. Instead, the settlement meaningfully recognized what the client was forced to endure.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">What Retained-Object Patients Should Understand</h2>
            <p className="text-slate-700 leading-relaxed">
              The value of a retained surgical-object case depends on several factors, including:
            </p>
            <ul className="text-slate-700 leading-relaxed space-y-2">
              <li>What object was left inside the body</li>
              <li>How long it remained there</li>
              <li>The severity and duration of the resulting pain</li>
              <li>Whether another surgery was required</li>
              <li>Whether the object caused infection or permanent injury</li>
              <li>Whether a defective surgical instrument or product contributed to the incident</li>
              <li>Which healthcare providers, facilities, manufacturers, and insurers may be responsible</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Even when the facts appear straightforward, identifying every potentially responsible party and understanding the applicable medical-malpractice law can materially affect the outcome.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If you discovered that a surgical instrument, sponge, needle, or other object was left inside your body, I can evaluate what happened and help you understand your options.
            </p>
          </div>
        </div>
      </article>

      {/* Legal Concepts callout — educational moment that reinforces expertise
          and internally links to the glossary. */}
      <LegalConceptsCallout
        concepts={[
          {
            term: 'Res ipsa loquitur',
            slug: 'res-ipsa-loquitur',
            latin: 'the thing speaks for itself',
            explanation:
              'A legal doctrine that allows negligence to be inferred when an accident is of a type that ordinarily would not occur without negligence. A retained surgical fragment is a classic example — a correct instrument count does not resolve the problem when a piece of the tool is missing and remains inside the patient.',
          },
          {
            term: 'Retained surgical object',
            slug: 'retained-surgical-object',
            explanation:
              'Any instrument, sponge, needle, or fragment of an instrument left inside a patient after surgery. Even a broken piece of an otherwise "accounted for" tool qualifies.',
          },
          {
            term: 'Product liability',
            slug: 'product-liability',
            explanation:
              "The legal responsibility of a manufacturer for injuries caused by a defective product. A tool that breaks during a routine procedure may share responsibility for the resulting harm — one reason I investigated the instrument's manufacturer, not just the hospital.",
          },
        ]}
      />

      {/* CTA */}
      <section className="py-16 md:py-20 bg-teal-600 text-white" data-testid="case-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <ShieldCheck className="w-12 h-12 mx-auto mb-5 text-teal-100" aria-hidden="true" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Think a Surgical Object Was Left Inside You?
          </h2>
          <p className="text-lg md:text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Contact Amy C. Brown Law for a free consultation. You pay no attorney fee unless I recover compensation for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:425-675-1224"
              data-testid="case-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-bold hover:bg-teal-50 transition shadow-lg"
            >
              <Phone className="w-5 h-5" /> Call (425) 675-1224
            </a>
            <a
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition"
            >
              <MessageCircle className="w-5 h-5" /> Text Me
            </a>
          </div>
        </div>
      </section>

      {/* Related resources */}
      <section className="py-14 md:py-16 bg-white" data-testid="case-related">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h3 className="font-heading text-2xl font-bold text-secondary mb-6 text-center">Related Resources</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/med-malpractice" className="group bg-slate-50 hover:bg-teal-50 border border-slate-100 rounded-2xl p-5 transition" data-testid="related-med-mal">
              <p className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">Practice area</p>
              <p className="font-semibold text-secondary group-hover:text-teal-700 flex items-center gap-2">
                Medical Malpractice — Clear Liability Cases <ArrowRight className="w-4 h-4" />
              </p>
            </Link>
            <Link to="/compensation-estimator" className="group bg-slate-50 hover:bg-teal-50 border border-slate-100 rounded-2xl p-5 transition">
              <p className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">Tool</p>
              <p className="font-semibold text-secondary group-hover:text-teal-700 flex items-center gap-2">
                What Is My Case Worth? Free Case Calculator <ArrowRight className="w-4 h-4" />
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Confidentiality / No-guarantee disclaimer */}
      <section className="py-10 bg-slate-50 border-t border-slate-100" data-testid="case-disclaimer">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
            <Search className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" aria-hidden="true" />
            <p>
              <strong className="text-secondary">Disclaimer:</strong> Every case is different. This result does not guarantee or predict the outcome of another case. Settlement details have been generalized to protect the client's identity and comply with the confidentiality terms of the agreement. Past results do not constitute a guarantee, warranty, or prediction regarding the outcome of your legal matter.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-links to other case results */}
      <RelatedCaseResults currentSlug="retained-surgical-tool-settlement" />
    </div>
  );
};

export default CaseResultRetainedSurgicalToolPage;
